import { useState } from 'react';
import { FieldValues, useController } from 'react-hook-form';
import { CurrencyInput, CurrencyInputProps } from '@spark-web/currency-input';
import { Field, FieldProps as SWFieldProps } from '@spark-web/field';
import { FormFieldProps } from '../Form';

export type CurrencyInputFieldProps<TFieldValues extends FieldValues = FieldValues> = FormFieldProps<TFieldValues> &
  CurrencyInputProps & { FieldProps?: Omit<SWFieldProps, 'label' | 'children'> };

function CurrencyInputField<TFieldValues extends FieldValues = FieldValues>({
  name,
  label,
  control,
  shouldUnregister,
  FieldProps,
  ...currencyInputProps
}: CurrencyInputFieldProps<TFieldValues>) {
  const {
    field: { value, onChange, ...field },
    fieldState: { error },
  } = useController<TFieldValues>({
    name,
    control,
    rules: {
      onBlur: currencyInputProps.onBlur,
    },
    shouldUnregister,
  });

  // * On initial render, if the underlying input value is falsy, the input should display nothing
  const [isPristine, setIsPristine] = useState(true);

  const handleChange = (value: string | number) => {
    if (isPristine) {
      setIsPristine(false);
    }

    const floatValue = typeof value === 'string' ? 0 : value;

    onChange(floatValue);

    currencyInputProps.onChange?.(floatValue);
  };

  return (
    <Field
      label={label}
      {...FieldProps}
      tone={error ? 'critical' : FieldProps?.tone}
      message={error?.message ?? FieldProps?.message}
    >
      <CurrencyInput
        {...currencyInputProps}
        {...field}
        value={isPristine && !value ? '' : value}
        onChange={handleChange}
      />
    </Field>
  );
}

export default CurrencyInputField;
